import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import Background from '../components/Background';
import logger from '../utils/logger';

// Planet agent data with pastel colors, symbols, and their astrological domains
const agents = [
  { name: 'Sun', symbol: '☉', color: '#FFE5A0', domain: 'Identity, core self, vitality, leadership' },
  { name: 'Moon', symbol: '☽', color: '#E8E4F0', domain: 'Emotions, intuition, needs, subconscious' },
  { name: 'Mercury', symbol: '☿', color: '#B8E6F5', domain: 'Communication, thinking, learning, wit' },
  { name: 'Venus', symbol: '♀', color: '#FFD4E5', domain: 'Love, beauty, values, relationships, harmony' },
  { name: 'Mars', symbol: '♂', color: '#FF9A8B', domain: 'Action, drive, passion, courage, assertion' },
  { name: 'Jupiter', symbol: '♃', color: '#C9B5E8', domain: 'Expansion, wisdom, optimism, abundance, philosophy' },
  { name: 'Saturn', symbol: '♄', color: '#9B7BC8', domain: 'Structure, discipline, responsibility, lessons, karma' },
  { name: 'Uranus', symbol: '⛢', color: '#7FE8E8', domain: 'Innovation, rebellion, freedom, sudden change, individuality' },
  { name: 'Neptune', symbol: '♆', color: '#9BB5FF', domain: 'Dreams, spirituality, intuition, creativity, mysticism' }
];

// Question/Response sets - rotating through different scenarios
const questionSets = [
  {
    question: 'Should I quit my job and start that side hustle I\'ve been dreaming about?',
    responses: {
      'Sun': 'Your Sun in the 10th house tells me you were built for leadership, not following. That corporate job? It\'s dimming your authentic light. Your side hustle is your destiny calling. Step into your power.',
      'Moon': 'Your gut has been telling you this for months, hasn\'t it? That nagging feeling isn\'t anxiety-it\'s your intuition. Your Moon\'s current transit calls you toward emotional fulfillment, not just financial security. You\'re running toward what your heart wants.',
      'Mercury': 'Let\'s be strategic. Have you mapped the first 90 days? The transition plan? Your Mercury placement gives you the analytical mind to make this work-but only if you use it. Think it through, then execute with precision.',
      'Venus': 'What does your heart value more-security or fulfillment? Your Venus placement shows you\'re being pulled between comfort and passion. That side hustle aligns with your true values. When you\'re doing what you love, the universe conspires to help. Trust that the money will follow the passion.',
      'Mars': 'Your Mars in the 10th house-this is literally your moment. You were meant for entrepreneurship, not employee status. Stop overthinking and start doing. You\'ve been planning this for how long? Action creates momentum. Your courage is your currency here. The stars are aligned for bold moves.',
      'Jupiter': 'This is your moment of expansion! Your Jupiter placement is activating growth opportunities right now. That side hustle could become something massive if you give it space. The universe is literally asking you to grow. Abundance flows to those who bet on themselves. Think bigger-way bigger.',
      'Saturn': 'Hold up. Your Saturn return is asking: can you sustain yourself for 6 months? Have you tested the market? Discipline now means freedom later. Build the structure first-Saturn rewards patience and planning.',
      'Uranus': 'Revolutionary move, I love it! Your Uranus placement in the 10th house-you were born for unconventional paths. Break free from the corporate cage. Your unique approach is what the world needs. Unconventional? That\'s your edge.',
      'Neptune': 'I see the vision-it\'s beautiful, but don\'t romanticize the struggle. Your Neptune energy shows you the dream clearly, but sometimes misses practical steps. Ground your dreams in reality while keeping that spark alive.'
    }
  },
  {
    question: 'Why do I keep attracting the same toxic relationship patterns?',
    responses: {
      'Sun': 'Your Sun in the 7th house-you\'re not broken, you\'re just not seeing your own worth. These patterns mirror what you believe you deserve. Time to rewrite that story. You attract what you think you\'re worth.',
      'Moon': 'Your Moon in the 12th house-your emotional patterns are stuck in a loop. That wound from childhood? It\'s calling the shots. Until you heal what\'s buried, you\'ll keep recreating it. Feel the feelings, break the cycle.',
      'Mercury': 'Let\'s analyze this logically. Your Mercury placement shows patterns clearly-so what red flags are you ignoring? Write them down. Your mind knows the pattern; you just need to listen. Stop making excuses for behavior you wouldn\'t accept from a friend.',
      'Venus': 'Your Venus in the 7th house-you\'re settling because you think love should be hard. Newsflash: healthy love feels easy. You\'re attracting what matches your self-worth. Raise your standards, and watch who shows up.',
      'Mars': 'Your Mars in the 7th house-you\'re not setting boundaries, and that\'s the problem. Toxic people sense that and move in. Time to get fierce. Say no. Walk away. Your peace is non-negotiable.',
      'Jupiter': 'This is growth disguised as a problem. Your Jupiter placement teaches self-love through contrast. Once you see what you don\'t want, you\'ll magnetize what you do. Expand your definition of what\'s possible in love.',
      'Saturn': 'Karmic lesson alert! Your Saturn in the 7th house-you keep attracting this because you haven\'t learned the lesson yet. What are you supposed to learn? Self-respect? Boundaries? Master this, and the pattern breaks.',
      'Uranus': 'Break the pattern, rebel! Your Uranus placement in the 7th house-you were meant for unconventional relationships. Stop doing what you\'ve always done. Try something completely different. Your old approach isn\'t working.',
      'Neptune': 'Your Neptune in the 7th house-you\'re seeing these people through rose-colored glasses, seeing potential instead of reality. Wake up from the fantasy. Love isn\'t about fixing someone. Real love meets you where you are, fully formed.'
    }
  },
  {
    question: 'I feel stuck in life-how do I find my purpose?',
    responses: {
      'Sun': 'Your Sun in the 9th house-your purpose isn\'t something you find, it\'s who you already are. Stop searching outside yourself. What makes you feel most alive? That\'s your North Star. Follow it-your chart shows you\'re meant to shine.',
      'Moon': 'That stuck feeling? Your Moon\'s current transit is your soul trying to get your attention. Your emotions are messengers. Listen to the whispers before they become screams. Your purpose lives in what moves you deeply.',
      'Mercury': 'You\'re overthinking this. Your Mercury placement shows purpose isn\'t one grand thing-it\'s how you show up every day. What problems do you naturally solve? Your purpose is in your unique way of thinking. Use that gift.',
      'Venus': 'Your Venus placement reveals what you value most. What brings you joy? Your purpose is wrapped in what you love. Follow the beauty. Life\'s too short to ignore what lights you up. Your values are your compass.',
      'Mars': 'Your Mars placement in fire signs or the 1st house-stop waiting for permission. Action creates purpose. Start moving in any direction that feels right. Momentum reveals the path. You\'ll discover it by doing.',
      'Jupiter': 'Your Jupiter in the 9th or 10th house-your purpose is bigger than you think. It\'s not just about you; it\'s about how you serve others. What wisdom do you have to share? Purpose lives in the intersection of your gifts and others\' needs.',
      'Saturn': 'Your Saturn placement is asking: what are you willing to work for? Purpose requires discipline. You can\'t find it if you\'re not willing to build it. Your purpose is in the daily practice, not the grand revelation. Saturn rewards the long game.',
      'Uranus': 'Your Uranus placement in Aquarius or the 11th house-your purpose is probably something unconventional that scares you. That\'s how you know it\'s right. Break free from what society says. Your unique path is your purpose. Embrace the weird.',
      'Neptune': 'Your Neptune in the 12th house-your purpose is a feeling, not a job title. It\'s in the moments you lose track of time. It\'s in your dreams and visions. Trust the mystery. Sometimes purpose finds you when you stop looking.'
    }
  },
  {
    question: 'Should I move to a new city or stay where I am?',
    responses: {
      'Sun': 'Your Sun in the 9th house-where do you feel most like yourself? Your identity isn\'t tied to a place, but some places let you shine brighter. If you\'re shrinking where you are, that\'s your answer. Your chart suggests you\'re meant to explore.',
      'Moon': 'What does your heart say when you imagine each option? Your Moon\'s current transit is giving you signals. That flutter of excitement or that sinking feeling? Your emotions are your GPS. Trust your gut-your Moon placement shows you need emotional alignment.',
      'Mercury': 'Let\'s make a pros and cons list. Your Mercury placement gives you the analytical mind to weigh this properly. What opportunities exist in each place? Your logical mind needs facts, but don\'t forget to factor in happiness as a variable.',
      'Venus': 'Your Venus placement shows what you value-which place aligns with that? Where can you create more beauty? Where do you feel more connected to what you love? Your heart knows. Follow the place that makes your soul feel at home.',
      'Mars': 'Your Mars in the 9th house-if you\'re asking, you probably already know. Something\'s calling you. Take action. You can always come back, but you can\'t get back the time you spend hesitating. Your chart shows you\'re built for bold moves.',
      'Jupiter': 'Your Jupiter placement in the 9th house-new city = new expansion. Staying = familiar comfort. Growth happens outside your comfort zone. That new city could open doors you don\'t even know exist yet. Think bigger-Jupiter is asking you to expand.',
      'Saturn': 'Practical matters first: job, housing, finances. Your Saturn placement demands structure. Can you make it work? Have you done the research? Moving is a big commitment-make sure the foundation is solid. Saturn rewards those who plan well.',
      'Uranus': 'Your Uranus placement in the 9th or 4th house-change is your friend here. Staying when you\'re feeling called elsewhere? That\'s resistance to growth. Break the pattern. The new city might be exactly the shake-up your soul needs.',
      'Neptune': 'Your Neptune energy-close your eyes. Picture yourself in each place. Which vision feels more aligned with your dreams? Sometimes the answer is in the feeling, not the logic. Your intuition knows. Listen to what it\'s showing you.'
    }
  },
  {
    question: 'How do I stop people-pleasing and start living for myself?',
    responses: {
      'Sun': 'Your Sun in the 12th house-you\'re living someone else\'s life, and your authentic self is suffocating. Every "yes" to others is a "no" to yourself. Start saying no. Your identity matters. Stop dimming your light-your chart shows you\'re meant to shine.',
      'Moon': 'That people-pleasing? Your Moon placement shows it\'s a survival strategy from when you needed approval to feel safe. You\'re not that child anymore. Your emotional needs are valid. It\'s okay to disappoint people. Honor your own needs first.',
      'Mercury': 'Here\'s the script: "I appreciate you thinking of me, but that doesn\'t work for me." Your Mercury placement gives you the words-practice them. Stop explaining yourself. "No" is a complete sentence. Your boundaries deserve clear communication.',
      'Venus': 'Your Venus in the 7th house-you think you\'re being kind, but you\'re actually being dishonest. Real relationships can handle your truth. If they can\'t, they weren\'t real anyway. Value yourself enough to show up authentically.',
      'Mars': 'Your Mars placement-time to get fierce about your boundaries. People-pleasing is passive aggression toward yourself. Stand up. Say no. Walk away if needed. Your peace is worth more than their approval. Stop fighting for people who drain you.',
      'Jupiter': 'Your Jupiter placement shows this is expansion, not contraction. Living for yourself doesn\'t mean being selfish-it means being whole. When you\'re full, you have more to give. You\'ll be a better friend when you\'re not running on empty.',
      'Saturn': 'Your Saturn placement-this is a discipline issue. You need to practice saying no like it\'s a muscle. Start small. Build the habit. It\'ll feel uncomfortable at first-that\'s growth. Structure your life around your needs, not others\' expectations.',
      'Uranus': 'Your Uranus placement in Aquarius or the 1st house-rebel against the conditioning! That "be nice" programming? Question it. Who benefits from you being a doormat? Break free. Your individuality is your power. Be unapologetically you.',
      'Neptune': 'Your Neptune in the 12th house-you\'re losing yourself in others\' needs, dissolving your boundaries. Wake up from the illusion that you\'re helping by sacrificing yourself. Real service comes from wholeness. Ground yourself in your own truth first.'
    }
  }
];

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { user, signInWithGoogle, signOut } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Animation state
  const [currentAgentIndex, setCurrentAgentIndex] = useState(0);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [isRising, setIsRising] = useState(false);
  const [hasStartedRising, setHasStartedRising] = useState(false);
  const [displayedText, setDisplayedText] = useState('');
  const [rotationAngle, setRotationAngle] = useState(0);
  const rotationAngleRef = useRef(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [currentQuestionSet, setCurrentQuestionSet] = useState(() => Math.floor(Math.random() * questionSets.length));
  
  
  // Handle window resize for responsive radius calculation
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Sync ref with state
  useEffect(() => {
    rotationAngleRef.current = rotationAngle;
  }, [rotationAngle]);

  useEffect(() => {
    logger.componentMount('Landing', { user: user?.email || 'not authenticated' });
    return () => {
      logger.componentUnmount('Landing');
    };
  }, [user?.email]);

  // Animation cycle
  useEffect(() => {
    const animationCycle = async () => {
      // Step 1: Start rise animation from ring position
      // First, render planet at ring position
      setIsRising(true);
      setHasStartedRising(false);
      setIsAgentSpeaking(true);
      
      // Small delay to ensure initial position is rendered at ring
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Now trigger the transition to top
      setHasStartedRising(true);
      
      // Wait for rise animation to complete (2 seconds - same as descent)
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mark that rising is complete
      setIsRising(false);
      setHasStartedRising(false);
      
      // Step 2: Typewriter effect for agent response
      const currentAgent = agents[currentAgentIndex];
      const questionSet = questionSets[currentQuestionSet];
      const text = questionSet.responses[currentAgent.name as keyof typeof questionSet.responses] || 'Insightful guidance awaits...';
      for (let i = 0; i <= text.length; i++) {
        setDisplayedText(text.slice(0, i));
        await new Promise(resolve => setTimeout(resolve, 35));
      }
      
      // Step 3: Hold for 2.5 seconds
      await new Promise(resolve => setTimeout(resolve, 2500));
      
      // Step 4: Start fade-out and descent animation
      setIsFadingOut(true);
      
      // Wait for descent animation to complete (2 seconds)
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Step 5: Hide elements and reset state (after planet is back in ring)
      setIsAgentSpeaking(false);
      setIsFadingOut(false);
      setDisplayedText('');
      
      // Wait a moment before rotation
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Step 6: Rotate ring to position next agent at top
      const nextIndex = (currentAgentIndex + 1) % agents.length;
      const anglePerAgent = 360 / agents.length;
      
      // Simply rotate by one step (anglePerAgent) to move next planet to top
      const rotationStep = -anglePerAgent; // Negative because we want to rotate counter-clockwise
      
      // Rotate ring by exactly one step
      setRotationAngle(prev => {
        const newAngle = prev + rotationStep;
        rotationAngleRef.current = newAngle;
        return newAngle;
      });
      
      // Wait for rotation to complete (1.5 seconds)
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Step 7: Move to next agent (will trigger next cycle)
      setCurrentAgentIndex(nextIndex);
      
      // Rotate to next question set after all agents have spoken
      if (nextIndex === 0) {
        setCurrentQuestionSet((prev) => (prev + 1) % questionSets.length);
      }
    };

    const timer = setTimeout(() => {
      animationCycle();
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAgentIndex]); // currentQuestionSet is intentionally not in deps to avoid re-triggering on question set change

  const handleGoogleSignIn = async () => {
    logger.userAction('Landing', 'handleGoogleSignIn');
    
    try {
      setIsLoading(true);
      setError('');
      logger.info('Landing', 'Initiating Google sign in');
      await signInWithGoogle();
      logger.info('Landing', 'Google sign in initiated successfully');
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to sign in with Google';
      setError(errorMessage);
      logger.error('Landing', 'Google sign in failed', err, { errorMessage });
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    logger.userAction('Landing', 'handleSignOut');
    try {
      setIsLoading(true);
      logger.info('Landing', 'Initiating sign out');
      await signOut();
      logger.info('Landing', 'Sign out completed successfully');
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to sign out';
      setError(errorMessage);
      logger.error('Landing', 'Sign out failed', err, { errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const currentAgent = agents[currentAgentIndex];

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: '#050508' }}>
      <Background />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-between py-6 md:py-8 px-4 sm:px-6">
        {/* Header Section */}
        <div className="text-center max-w-4xl w-full mb-2 md:mb-3">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-gradient-gold mb-2 md:mb-3 tracking-luxury">
            Aivaris
        </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-cosmic-gold/80 font-elegant tracking-luxury-tight italic">
            Your Questions. Your Stars. Your Path.
          </p>
        </div>

        {/* Animation Section */}
        <div className="flex-1 flex items-center justify-center w-full max-w-5xl py-2 md:py-4">
          <div className="relative w-full flex flex-col items-center">
            {/* Speaking Agent Blurb - Centered above planet ring, won't affect layout */}
            <div className="w-full flex items-center justify-center mb-3 md:mb-4 min-h-[120px] sm:min-h-[140px] md:min-h-[160px]">
              {isAgentSpeaking && (
                <div 
                  className={`glass-grain rounded-2xl px-6 py-5 max-w-xl w-[280px] sm:w-[320px] md:w-[400px] backdrop-blur-xl border shadow-2xl z-20 transition-opacity duration-[2000ms] ease-in-out ${
                    isFadingOut ? 'animate-fade-out opacity-0' : 'animate-fade-in opacity-100'
                  }`}
                  style={{
                    borderColor: `${currentAgent.color}40`,
                    backgroundColor: `${currentAgent.color}08`,
                    boxShadow: `0 0 40px ${currentAgent.color}15, 0 8px 32px rgba(0,0,0,0.4)`
                  }}
                >
                  <div className="flex items-center gap-3 mb-3 relative z-10">
                    <div 
                      className="w-3 h-3 rounded-full animate-pulse"
                      style={{ 
                        backgroundColor: currentAgent.color,
                        boxShadow: `0 0 12px ${currentAgent.color}`
                      }}
                    />
                    <span 
                      className="font-heading text-sm tracking-luxury-tight font-medium"
                      style={{ color: currentAgent.color }}
                    >
                      {currentAgent.name}
                    </span>
                  </div>
                  <p className="text-text-primary font-elegant text-sm sm:text-base leading-relaxed tracking-luxury-tight relative z-10">
                    {displayedText}
                    <span className="inline-block w-1.5 h-4 ml-1 bg-current animate-pulse" />
                  </p>
                </div>
              )}
            </div>
            
            {/* Planet Ring - Fixed position, won't move */}
            <div className="relative w-[220px] h-[220px] sm:w-[270px] sm:h-[270px] md:w-[340px] md:h-[340px]">
              {/* Rotating container - only non-active planets */}
              <div 
                className="absolute inset-0 transition-transform duration-[1500ms] ease-in-out"
                style={{ transform: `rotate(${rotationAngle}deg)` }}
              >
                {agents.map((agent, index) => {
                  // Skip active planet in rotating container (during rising, speaking, and descent)
                  if (index === currentAgentIndex && (isAgentSpeaking || isFadingOut || isRising)) return null;
                  
                  const angle = (index / agents.length) * 2 * Math.PI - Math.PI / 2;
                  // Responsive radius based on screen size
                  const radius = windowWidth < 640 ? 70 : windowWidth < 768 ? 90 : 115;
                  const x = Math.cos(angle) * radius;
                  const y = Math.sin(angle) * radius;

                  return (
                    <div
                      key={agent.name}
                      className="absolute transition-all duration-[1200ms] ease-in-out"
                      style={{
                        left: `calc(50% + ${x}px)`,
                        top: `calc(50% + ${y}px)`,
                        transform: `translate(-50%, -50%) rotate(${-rotationAngle}deg)`,
                        zIndex: 5,
                        opacity: 0.75
                      }}
                    >
                      {/* Outline circle with symbol inside - rustic style */}
                      <div
                        className="w-8 h-8 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-700 relative"
                        style={{
                          border: `2.5px solid ${agent.color}`,
                          backgroundColor: 'transparent',
                          boxShadow: `0 0 12px ${agent.color}70, 0 0 25px ${agent.color}40`
                        }}
                      >
                        {/* Planet symbol */}
                        <span 
                          className="text-base sm:text-lg md:text-xl font-medium transition-all duration-700"
                          style={{ 
                            color: agent.color,
                            textShadow: `0 0 8px ${agent.color}60`
                          }}
                        >
                          {agent.symbol}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
        
              {/* Active planet - rendered OUTSIDE rotating container, fixed at true center-top */}
              {/* Keep visible during rise and descent animations */}
              {(isAgentSpeaking || isFadingOut || isRising) && (() => {
                // Planet returns to top of ring (its position before rising)
                // Top of ring is at: 50% - radius (since top is -90 degrees, sin(-90) = -1)
                const radius = windowWidth < 640 ? 70 : windowWidth < 768 ? 90 : 115;
                const ringTopPosition = `calc(50% - ${radius}px)`;
                
                return (
                  <div
                    className="absolute left-1/2 transition-all duration-[2000ms] ease-in-out"
                    style={{
                      transform: 'translate(-50%, -50%)',
                      // When rising: start at ring position, then transition to top when hasStartedRising is true
                      // When not rising but speaking: stay at top
                      // When fading out: animate from top to ring
                      top: (isRising && !hasStartedRising) ? ringTopPosition : 
                           (isRising && hasStartedRising) ? '-60px' :
                           (isFadingOut ? ringTopPosition : '-60px'),
                      zIndex: isFadingOut ? 10 : 15,
                      opacity: isFadingOut ? 0.75 : 1
                    }}
                  >
                  {/* Outline circle with symbol inside - rustic style */}
                  <div
                    className={`w-8 h-8 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-[2000ms] relative ${
                      isFadingOut ? '' : 'animate-fade-in'
                    }`}
                    style={{
                      border: `2.5px solid ${agents[currentAgentIndex].color}`,
                      backgroundColor: 'transparent',
                      boxShadow: isFadingOut 
                        ? `0 0 12px ${agents[currentAgentIndex].color}70, 0 0 25px ${agents[currentAgentIndex].color}40`
                        : (isRising && !hasStartedRising)
                        ? `0 0 12px ${agents[currentAgentIndex].color}70, 0 0 25px ${agents[currentAgentIndex].color}40`
                        : `0 0 25px ${agents[currentAgentIndex].color}80, 0 0 50px ${agents[currentAgentIndex].color}60, inset 0 0 20px ${agents[currentAgentIndex].color}20`,
                      animation: (isFadingOut || (isRising && !hasStartedRising)) ? 'none' : 'pulse 2.5s ease-in-out infinite',
                      transform: (isRising && !hasStartedRising) ? 'scale(1)' : (isFadingOut ? 'scale(1)' : 'scale(1.3)')
                    }}
                  >
                    {/* Planet symbol */}
                    <span 
                      className="text-base sm:text-lg md:text-xl font-medium transition-all duration-700"
                      style={{ 
                        color: agents[currentAgentIndex].color,
                        textShadow: isFadingOut
                          ? `0 0 8px ${agents[currentAgentIndex].color}60`
                          : `0 0 15px ${agents[currentAgentIndex].color}, 0 0 30px ${agents[currentAgentIndex].color}80`,
                        filter: isFadingOut ? 'brightness(1)' : 'brightness(1.2)'
                      }}
                    >
                      {agents[currentAgentIndex].symbol}
                    </span>
                  </div>
                </div>
                );
              })()}

              {/* Center glow */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div 
                  className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full transition-all duration-1000"
                  style={{
                    background: `radial-gradient(circle, ${currentAgent.color}25 0%, ${currentAgent.color}10 40%, transparent 70%)`,
                    filter: 'blur(25px)',
                    animation: 'pulse-glow 3s ease-in-out infinite'
                  }}
                />
              </div>

              {/* User Prompt - Glass Card - Bottom aligned with planet ring */}
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 z-30 w-full max-w-[240px] sm:max-w-[290px] md:max-w-[360px]">
                <div 
                  key={currentQuestionSet}
                  className="glass-grain rounded-2xl px-5 md:px-6 py-3 md:py-4 backdrop-blur-xl border-2 border-cosmic-gold/30 shadow-2xl transform hover:scale-[1.02] transition-transform duration-300 animate-fade-in"
                >
                  <p className="text-text-primary font-elegant text-sm md:text-base lg:text-lg tracking-luxury-tight text-center relative z-10 font-medium">
                    "{questionSets[currentQuestionSet].question}"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center max-w-md w-full mt-4 md:mt-6 px-4">
        {error && (
            <div className="mb-4 p-4 glass-grain rounded-lg border border-red-500/30 backdrop-blur-xl">
              <p className="text-red-400 font-elegant tracking-luxury-tight text-sm">{error}</p>
          </div>
        )}
        
          {user ? (
            <div className="space-y-3">
              <p className="text-text-secondary mb-3 font-elegant tracking-luxury-tight text-xs sm:text-sm">
                Signed in as {user.email}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  onClick={() => {
                    logger.userAction('Landing', 'navigateToDashboard');
                    navigate('/auth-redirect');
                  }}
                  className="min-w-[160px] sm:min-w-[180px]"
                >
                  Continue to Aivaris
                </Button>
                <Button 
                  onClick={handleSignOut}
                  isLoading={isLoading}
                  variant="secondary"
                  className="min-w-[160px] sm:min-w-[180px] text-base md:text-lg py-2.5 md:py-3 glass-grain border border-cosmic-gold/40 hover:border-cosmic-gold/60 bg-space-dark/40 hover:bg-space-dark/60 backdrop-blur-xl transition-all duration-300 hover:scale-[1.02]"
                >
                  Sign Out
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 md:space-y-4">
              <div className="flex items-center justify-center">
            <Button 
              onClick={handleGoogleSignIn}
              isLoading={isLoading}
                  variant="secondary"
                  className="w-full sm:w-auto min-w-[240px] sm:min-w-[260px] md:min-w-[280px] text-base md:text-lg py-2.5 md:py-3 glass-grain border border-cosmic-gold/40 hover:border-cosmic-gold/60 bg-space-dark/40 hover:bg-space-dark/60 backdrop-blur-xl transition-all duration-300 flex items-center justify-center gap-2 group/btn"
            >
                  {isLoading ? 'Signing in...' : (
                    <>
                      Talk to Aivaris
                      <svg 
                        className="w-5 h-5 text-cosmic-gold group-hover/btn:translate-x-1 transition-transform duration-300" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </>
                  )}
            </Button>
              </div>
              <div className="flex items-center justify-center">
                <div className="glass-grain rounded-lg px-4 py-2 inline-block border border-cosmic-gold/30 backdrop-blur-xl">
                  <p className="text-xs sm:text-sm text-cosmic-gold font-elegant tracking-luxury-tight text-center">
                    ✨ Free to start • 5 questions free monthly
                  </p>
                </div>
              </div>
            </div>
          )}
        
          <p className="mt-3 md:mt-4 text-[10px] sm:text-xs text-text-tertiary font-elegant tracking-luxury-tight opacity-70 text-center">
            By signing in, you agree to our{' '}
            <Link to="/terms-and-conditions" className="text-cosmic-gold hover:text-cosmic-gold/80 transition-colors duration-300 underline">
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link to="/privacy-policy" className="text-cosmic-gold hover:text-cosmic-gold/80 transition-colors duration-300 underline">
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
      
      {/* Content Sections - Simplified & Cleaner */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-24 space-y-24 md:space-y-32">
        
        {/* How It Works - Streamlined */}
        <section className="text-center max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading text-gradient-gold mb-4 tracking-luxury font-semibold">
            How It Works
          </h2>
          <p className="text-sm sm:text-base text-text-secondary/70 font-sans mb-12 max-w-2xl mx-auto">
            Trained on thousands of astrological datapoints for personalized insights
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full border border-cosmic-gold/30 flex items-center justify-center text-cosmic-gold font-sans text-sm font-medium">
                01
              </div>
              <h3 className="font-heading text-gradient-gold mb-3 text-lg">Ask</h3>
              <p className="text-sm text-text-secondary/80 font-sans leading-relaxed">
                Pose any question about your life, relationships, or future
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full border border-cosmic-gold/30 flex items-center justify-center text-cosmic-gold font-sans text-sm font-medium">
                02
              </div>
              <h3 className="font-heading text-gradient-gold mb-3 text-lg">Analyze</h3>
              <p className="text-sm text-text-secondary/80 font-sans leading-relaxed">
                Nine planetary agents examine your unique birth chart
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full border border-cosmic-gold/30 flex items-center justify-center text-cosmic-gold font-sans text-sm font-medium">
                03
              </div>
              <h3 className="font-heading text-gradient-gold mb-3 text-lg">Receive</h3>
              <p className="text-sm text-text-secondary/80 font-sans leading-relaxed">
                Get personalized insights from multiple perspectives
              </p>
            </div>
          </div>
        </section>

        {/* Key Features - Grid Layout */}
        <section className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <div className="glass-grain rounded-xl p-6 md:p-8 backdrop-blur-xl border border-cosmic-gold/20 transition-all duration-300 hover:border-cosmic-gold/40 hover:shadow-[0_0_20px_rgba(255,184,79,0.1)]" style={{ background: 'rgba(20, 20, 32, 0.3)' }}>
              <div className="w-8 h-8 rounded-full bg-cosmic-gold/10 border border-cosmic-gold/30 flex items-center justify-center mb-4">
                <svg className="w-4 h-4 text-cosmic-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-heading text-gradient-gold text-xl mb-3">Personalized Analysis</h3>
              <p className="text-sm text-text-secondary/80 font-sans leading-relaxed">
                Every reading is based on your exact birth time, location, and current planetary transits—not generic horoscopes.
              </p>
            </div>
            
            <div className="glass-grain rounded-xl p-6 md:p-8 backdrop-blur-xl border border-cosmic-gold/20 transition-all duration-300 hover:border-cosmic-gold/40 hover:shadow-[0_0_20px_rgba(255,184,79,0.1)]" style={{ background: 'rgba(20, 20, 32, 0.3)' }}>
              <div className="w-8 h-8 rounded-full bg-cosmic-gold/10 border border-cosmic-gold/30 flex items-center justify-center mb-4">
                <svg className="w-4 h-4 text-cosmic-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="font-heading text-gradient-gold text-xl mb-3">Multi-Agent Insights</h3>
              <p className="text-sm text-text-secondary/80 font-sans leading-relaxed">
                Nine distinct planetary perspectives analyze your question, giving you comprehensive understanding from every angle.
              </p>
            </div>
            
            <div className="glass-grain rounded-xl p-6 md:p-8 backdrop-blur-xl border border-cosmic-gold/20 transition-all duration-300 hover:border-cosmic-gold/40 hover:shadow-[0_0_20px_rgba(255,184,79,0.1)]" style={{ background: 'rgba(20, 20, 32, 0.3)' }}>
              <div className="w-8 h-8 rounded-full bg-cosmic-gold/10 border border-cosmic-gold/30 flex items-center justify-center mb-4">
                <svg className="w-4 h-4 text-cosmic-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="font-heading text-gradient-gold text-xl mb-3">Data-Driven Accuracy</h3>
              <p className="text-sm text-text-secondary/80 font-sans leading-relaxed">
                Trained on thousands of astrological datapoints, historical patterns, and psychological correlations for precise insights.
              </p>
            </div>
            
            <div className="glass-grain rounded-xl p-6 md:p-8 backdrop-blur-xl border border-cosmic-gold/20 transition-all duration-300 hover:border-cosmic-gold/40 hover:shadow-[0_0_20px_rgba(255,184,79,0.1)]" style={{ background: 'rgba(20, 20, 32, 0.3)' }}>
              <div className="w-8 h-8 rounded-full bg-cosmic-gold/10 border border-cosmic-gold/30 flex items-center justify-center mb-4">
                <svg className="w-4 h-4 text-cosmic-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-heading text-gradient-gold text-xl mb-3">Real-Time Transits</h3>
              <p className="text-sm text-text-secondary/80 font-sans leading-relaxed">
                Current planetary positions are calculated with mathematical precision using live astronomical data.
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA - Minimalist */}
        <section className="text-center pt-8 max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading text-gradient-gold mb-6 tracking-luxury font-semibold">
            Start Your Journey
          </h2>
          <p className="text-sm sm:text-base text-text-secondary/70 font-sans mb-10 max-w-xl mx-auto">
            Join thousands discovering their cosmic blueprint with personalized astrological insights
          </p>
          
          {!user && (
            <div className="flex flex-col items-center gap-4">
              <Button 
                onClick={handleGoogleSignIn}
                isLoading={isLoading}
                variant="secondary"
                className="min-w-[240px] sm:min-w-[280px] text-base md:text-lg py-3.5 md:py-4 glass-grain border border-cosmic-gold/40 hover:border-cosmic-gold bg-space-dark/30 hover:bg-space-dark/50 backdrop-blur-xl transition-all duration-300 hover:scale-[1.02] group/btn font-sans"
              >
                <span className="flex items-center gap-2.5">
                  {isLoading ? 'Signing in...' : 'Talk to Aivaris'}
                  {!isLoading && (
                    <svg 
                      className="w-4 h-4 group-hover/btn:translate-x-0.5 transition-transform duration-300" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  )}
                </span>
              </Button>
              <p className="text-xs text-text-tertiary/60 font-sans">
                Free to start • 5 questions monthly
              </p>
            </div>
          )}
        </section>
      </div>

      {/* Footer */}
      <footer className="relative z-10 w-full border-t border-cosmic-gold/20 mt-16 md:mt-24 py-8 md:py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-8">
            <div className="text-center md:text-left">
              <p className="text-xs sm:text-sm text-text-tertiary/60 font-sans">
                © {new Date().getFullYear()} Aivaris. All rights reserved.
              </p>
            </div>
            <div className="flex items-center gap-6 md:gap-8">
              <Link
                to="/privacy-policy"
                className="text-xs sm:text-sm text-text-secondary/70 hover:text-cosmic-gold transition-colors duration-300 font-elegant tracking-luxury-tight"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-and-conditions"
                className="text-xs sm:text-sm text-text-secondary/70 hover:text-cosmic-gold transition-colors duration-300 font-elegant tracking-luxury-tight"
              >
                Terms & Conditions
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
